import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'app' | 'container' | 'form';
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[ErrorBoundary:${this.props.level ?? 'unknown'}]`, error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { level } = this.props;

    if (level === 'form') {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="300px"
          textAlign="center"
          p={4}
        >
          <Typography variant="h6" fontWeight="bold" mb={1}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            We encountered an issue loading this step. Please try again.
          </Typography>
          <Button variant="contained" onClick={this.handleReset}>
            Try Again
          </Button>
        </Box>
      );
    }

    if (level === 'container') {
      return (
        <Container maxWidth="md">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
            textAlign="center"
            p={4}
          >
            <Typography variant="h5" fontWeight="bold" mb={2}>
              Unable to load results
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              We're having trouble displaying this content. Please refresh the page or try again later.
            </Typography>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Box>
        </Container>
      );
    }

    // App-level fallback
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
        p={4}
        bgcolor="#f5f5f5"
      >
        <Typography variant="h4" fontWeight="bold" mb={2}>
          Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4} maxWidth="480px">
          An unexpected error occurred. Please refresh the page. If the problem persists, contact support.
        </Typography>
        <Button variant="contained" size="large" onClick={() => window.location.reload()}>
          Refresh Page
        </Button>
      </Box>
    );
  }
}

export default ErrorBoundary;
